import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { IoChevronDown, IoChevronUp, IoAdd } from 'react-icons/io5';
import { useAuth } from '../../../context/AuthContext';
import { getMyInquiries, STATUS_META, INQUIRY_TYPES } from '../../../services/inquiryService';

const CATEGORIES = ['전체', '#가망고객', '#밴딩', '#계좌인증', '#회원가입'];

const CATEGORY_BADGE = {
  signup: { label: '가입', bg: '#22C55E' },
  settlement: { label: '정산', bg: '#F59E0B' },
  db: { label: 'DB', bg: '#3B82F6' },
  partner: { label: '파트너', bg: '#8B5CF6' },
};

const FAQ_DATA = [
  {
    id: 1, category: 'signup',
    question: '공인중개사로 가입하려면?',
    answer: 'QR코드 스캔 또는 추천 링크를 통해 가입할 수 있습니다. 궁금하신 내용이 있으시면 고객센터를 이용해주세요.',
    date: '2026-02-24',
  },
  {
    id: 2, category: 'settlement',
    question: '수수료 정산은 언제 되나요?',
    answer: '매월 말일 기준으로 정산되며, 익월 초에 출금 신청이 가능합니다.',
    date: '2026-02-24',
  },
  {
    id: 3, category: 'db',
    question: 'DB 구매 후 취소가 가능한가요?',
    answer: '구매 후 24시간 이내에 취소 요청이 가능합니다. 고객센터로 문의해주세요.',
    date: '2026-02-24',
  },
  {
    id: 4, category: 'partner',
    question: '저를 통해 신청한지는 어떻게 확인하죠?',
    answer: '관리·정산 메뉴에서 고객 DB 추적을 통해 확인할 수 있습니다.',
    date: '2026-02-24',
  },
  {
    id: 5, category: 'partner',
    question: '고객이 다른 경로로 신청하면 어떻게 되나요?',
    answer: '고객이 다른 경로로 신청하더라도 최초 추천인에게 수수료가 지급됩니다.',
    date: '2026-02-24',
  },
];

const InquiryList = () => {
  const navigate = useNavigate();
  const { userData } = useAuth();
  const [tab, setTab] = useState('my'); // my | faq
  const [catIdx, setCatIdx] = useState(0);
  const [openId, setOpenId] = useState(1);
  const [myList, setMyList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openMyId, setOpenMyId] = useState(null);

  useEffect(() => {
    if (tab !== 'my' || !userData?.uid) return;
    setLoading(true);
    getMyInquiries(userData.uid)
      .then(setMyList)
      .finally(() => setLoading(false));
  }, [tab, userData?.uid]);

  const typeLabel = (k) => INQUIRY_TYPES.find((t) => t.key === k)?.label || '기타';

  return (
    <Container>
      <TabRow>
        <TabBtn $active={tab === 'my'} onClick={() => setTab('my')}>내 문의</TabBtn>
        <TabBtn $active={tab === 'faq'} onClick={() => setTab('faq')}>자주 묻는 질문</TabBtn>
      </TabRow>

      {tab === 'my' && (
        <>
          <NewBtn onClick={() => navigate('/realtor/inquiry/new')}>
            <IoAdd size={18} /> 새 문의 등록
          </NewBtn>
          {loading ? (
            <EmptyWrap>불러오는 중...</EmptyWrap>
          ) : myList.length === 0 ? (
            <EmptyWrap>아직 등록한 문의가 없습니다.</EmptyWrap>
          ) : (
            <FaqList>
              {myList.map((inq) => {
                const isOpen = openMyId === inq.id;
                const st = STATUS_META[inq.status] || STATUS_META.pending;
                return (
                  <FaqItem key={inq.id}>
                    <FaqHeader onClick={() => setOpenMyId(isOpen ? null : inq.id)}>
                      <FaqLeft>
                        <Badge $bg={st.color}>{st.label}</Badge>
                        <FaqQuestion>{inq.title || '(제목 없음)'}</FaqQuestion>
                        <FaqDate>{typeLabel(inq.type)} · {inq.createdAt}</FaqDate>
                      </FaqLeft>
                      {isOpen ? <IoChevronUp size={18} color="#9CA3AF" /> : <IoChevronDown size={18} color="#9CA3AF" />}
                    </FaqHeader>
                    {isOpen && (
                      <FaqAnswer>
                        <strong>문의 내용</strong>
                        <div style={{ marginTop: 6, marginBottom: inq.answer ? 12 : 0, whiteSpace: 'pre-wrap' }}>{inq.content}</div>
                        {inq.answer && (
                          <>
                            <strong>답변</strong>
                            <div style={{ marginTop: 6, color: '#166534', whiteSpace: 'pre-wrap' }}>{inq.answer}</div>
                          </>
                        )}
                      </FaqAnswer>
                    )}
                  </FaqItem>
                );
              })}
            </FaqList>
          )}
        </>
      )}

      {tab === 'faq' && (
        <>
          <ChipRow>
            {CATEGORIES.map((c, i) => (
              <Chip key={c} $active={catIdx === i} onClick={() => setCatIdx(i)}>{c}</Chip>
            ))}
          </ChipRow>
          <FaqList>
            {FAQ_DATA.map((faq) => {
              const badge = CATEGORY_BADGE[faq.category];
              const isOpen = openId === faq.id;
              return (
                <FaqItem key={faq.id}>
                  <FaqHeader onClick={() => setOpenId(isOpen ? null : faq.id)}>
                    <FaqLeft>
                      <Badge $bg={badge.bg}>{badge.label}</Badge>
                      <FaqQuestion>{faq.question}</FaqQuestion>
                      <FaqDate>최종 수정일: {faq.date}</FaqDate>
                    </FaqLeft>
                    {isOpen ? <IoChevronUp size={18} color="#9CA3AF" /> : <IoChevronDown size={18} color="#9CA3AF" />}
                  </FaqHeader>
                  {isOpen && <FaqAnswer>{faq.answer}</FaqAnswer>}
                </FaqItem>
              );
            })}
          </FaqList>
        </>
      )}
    </Container>
  );
};

export default InquiryList;

const Container = styled.div`
  padding-bottom: ${({ theme }) => theme.spacing.lg};
`;

const TabRow = styled.div`
  display: flex;
  gap: 8px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  margin-bottom: ${({ theme }) => theme.spacing.base};
`;

const TabBtn = styled.button`
  flex: 1;
  padding: 12px 0;
  background: none;
  border: none;
  border-bottom: 2px solid ${({ $active, theme }) => ($active ? theme.colors.primary : 'transparent')};
  color: ${({ $active, theme }) => ($active ? theme.colors.primary : theme.colors.textSecondary)};
  font-size: 14px;
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  cursor: pointer;
  margin-bottom: -1px;
`;

const NewBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 8px 14px;
  border: 1px solid ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  margin-bottom: 14px;
`;

const EmptyWrap = styled.div`
  padding: 60px 0;
  text-align: center;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 14px;
`;

const ChipRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  flex-wrap: wrap;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const Chip = styled.button`
  padding: 6px 14px;
  border: 1px solid ${({ $active, theme }) => $active ? theme.colors.primary : theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.pill};
  background: ${({ $active, theme }) => $active ? theme.colors.primary : theme.colors.bgWhite};
  color: ${({ $active }) => $active ? '#fff' : '#6B7280'};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  cursor: pointer;
  white-space: nowrap;
`;

const FaqList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
`;

const FaqItem = styled.div`
  background: ${({ theme }) => theme.colors.bgWhite};
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  overflow: hidden;
`;

const FaqHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing.base};
  cursor: pointer;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const FaqLeft = styled.div`
  flex: 1;
  min-width: 0;
`;

const Badge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: ${({ theme }) => theme.fontWeight.regular};
  color: #fff;
  background: ${({ $bg }) => $bg};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const FaqQuestion = styled.p`
  font-size: ${({ theme }) => theme.fontSize.base};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const FaqDate = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const FaqAnswer = styled.div`
  padding: 0 ${({ theme }) => theme.spacing.base} ${({ theme }) => theme.spacing.base};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.5;
`;
